import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Clock, Check, MapPin, ArrowLeft, Share2, Link2 } from 'lucide-react';
import { toast } from 'sonner';
import { MapEmbed } from '../components/MapEmbed';
import { WishlistButton } from '../components/WishlistButton';
import { useWishlist } from '../hooks/useWishlist';
import { api } from '../api';
import { Price } from '../context/CurrencyContext';
import { useLang } from '../context/LanguageContext';

const shareText = (pkg) =>
  `Check out this trip from TripSoul Travels: ${pkg.name} — ${pkg.duration} from ₹${pkg.price.toLocaleString()} per person. Full itinerary: ${window.location.href}`;

const SharePackage = ({ pkg }) => {
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      toast.success('Link copied to clipboard');
    } catch {
      toast.error('Could not copy link');
    }
  };
  return (
    <div className="flex gap-3" data-testid="share-package">
      <a
        data-testid="share-whatsapp-btn"
        href={`https://wa.me/?text=${encodeURIComponent(shareText(pkg))}`}
        target="_blank"
        rel="noopener noreferrer"
        className="flex-1 inline-flex items-center justify-center gap-2 rounded-md bg-[#25D366] hover:bg-[#1ebe5d] text-white font-semibold text-sm h-10 transition-colors"
      >
        <Share2 className="w-4 h-4" /> Share on WhatsApp
      </a>
      <Button data-testid="copy-link-btn" type="button" variant="outline" onClick={copy} className="border-2 h-10" aria-label="Copy link">
        <Link2 className="w-4 h-4" />
      </Button>
    </div>
  );
};

const PackageDetail = ({ user }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const wishlist = useWishlist(user);
  const { t, loc } = useLang();
  const [pkg, setPkg] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    api.get(`/packages/${id}`).then((res) => setPkg(res.data)).catch(() => setError(true));
  }, [id]);

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center pt-16">
        <div className="text-center" data-testid="package-not-found">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Package not found</h2>
          <Button onClick={() => navigate('/packages')} variant="outline">Back to Packages</Button>
        </div>
      </div>
    );
  }

  if (!pkg) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center pt-16">
        <p data-testid="package-loading" className="text-gray-600 text-lg">Loading itinerary...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pt-16" data-testid="package-detail-page">
      <div className="relative h-[420px] overflow-hidden">
        <img src={pkg.image} alt={pkg.name} className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-gray-900/90 via-gray-900/40 to-transparent" />
        <div className="absolute top-6 right-6 z-10">
          <WishlistButton active={wishlist.has('package', pkg.id)} onClick={() => wishlist.toggle('package', pkg.id)} testId="wishlist-package-detail" />
        </div>
        <div className="absolute inset-0 flex items-end">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12 w-full text-white">
            <button
              data-testid="package-back-btn"
              onClick={() => navigate('/packages')}
              className="inline-flex items-center text-teal-200 hover:text-white mb-4 text-sm"
            >
              <ArrowLeft className="w-4 h-4 mr-1" /> All packages
            </button>
            <h1 className="text-4xl sm:text-5xl font-bold mb-3" data-testid="package-detail-name">{loc(pkg, 'name')}</h1>
            <div className="flex flex-wrap items-center gap-6 text-teal-50">
              <span className="flex items-center"><MapPin className="w-4 h-4 mr-1" />{pkg.destinations.join(' • ')}</span>
              <span className="flex items-center"><Clock className="w-4 h-4 mr-1" />{pkg.duration}</span>
              <span className="flex items-center font-semibold text-xl"><Price inr={pkg.price} /> <span className="text-sm font-normal ml-1">/ person</span></span>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 grid grid-cols-1 lg:grid-cols-3 gap-10">
        <div className="lg:col-span-2 space-y-10">
          {pkg.overview && (
            <p className="text-lg text-gray-600 leading-relaxed" data-testid="package-overview">{loc(pkg, 'overview')}</p>
          )}

          <section>
            <h2 className="text-3xl font-bold text-gray-900 mb-8">Day-by-Day Itinerary</h2>
            <ol className="relative border-l-2 border-teal-200 ml-4 space-y-8" data-testid="package-itinerary">
              {pkg.itinerary.map((day) => (
                <li key={day.day} className="ml-8 relative" data-testid={`itinerary-day-${day.day}`}>
                  <span className="absolute -left-[46px] top-0 w-9 h-9 rounded-full bg-gradient-to-br from-teal-500 to-cyan-600 text-white text-sm font-bold flex items-center justify-center shadow">
                    {day.day}
                  </span>
                  <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 hover:shadow-md transition-shadow">
                    <p className="text-xs uppercase tracking-wider text-teal-600 font-semibold mb-1">Day {day.day}</p>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">{loc(day, 'title')}</h3>
                    <p className="text-gray-600">{loc(day, 'description')}</p>
                  </div>
                </li>
              ))}
            </ol>
          </section>
        </div>

        <aside className="space-y-6">
          <Card className="border-0 shadow-xl sticky top-24">
            <CardContent className="p-6 space-y-6">
              <div>
                <h4 className="font-semibold text-gray-900 mb-3">Package Includes</h4>
                <ul className="space-y-2">
                  {pkg.includes.map((item) => (
                    <li key={item} className="flex items-center text-sm text-gray-600">
                      <Check className="w-4 h-4 text-teal-600 mr-2" />{item}
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-3">Highlights</h4>
                <div className="flex flex-wrap gap-2">
                  {pkg.highlights.map((h) => (
                    <span key={h} className="bg-teal-50 text-teal-700 px-3 py-1 rounded-full text-sm">{h}</span>
                  ))}
                </div>
              </div>
              <MapEmbed query={pkg.destinations.join(', ')} zoom={6} className="w-full h-44 rounded-lg" testId="package-detail-map" />
              <Button
                data-testid="package-detail-book-btn"
                onClick={() => navigate('/booking', { state: { package: pkg } })}
                className="w-full bg-sun-500 hover:bg-sun-600 text-white py-6 text-lg"
              >
                {t('bookPackage')}
              </Button>
              <SharePackage pkg={pkg} />
            </CardContent>
          </Card>
        </aside>
      </div>
    </div>
  );
};

export default PackageDetail;
